export interface DialoguePolishPromptItem {
  order: number;
  name?: string | null;
  dialogue?: string | null;
  emotion?: string | null;
  notes?: string | null;
}

export const DEFAULT_DIALOGUE_POLISH_REQUEST = '请润色以下对白';
export const DEFAULT_VOICE_GENERATION_REQUEST = '请基于这条对白生成自然口播配音';

const VOICE_PROMPT_TYPE_TAG_RE = /^\s*(?:\[(?:对白|普通对白|旁白|内心(?:OS|os))\]\s*)+/u;

function buildVoicePromptPrefixes(options?: {
  characterName?: string | null;
  dialogueType?: 1 | 2 | 3;
}): string[] {
  const prefixes = new Set<string>();
  const characterName = options?.characterName?.trim();
  const dialogueType = options?.dialogueType;

  const addPrefix = (prefix: string) => {
    prefixes.add(`${prefix}：`);
    prefixes.add(`${prefix}:`);
  };

  if (characterName) {
    addPrefix(characterName);
    if (dialogueType === 2) {
      addPrefix(`${characterName}（内心）`);
      addPrefix(`${characterName}(内心)`);
    }
    if (dialogueType === 3) {
      addPrefix(`${characterName}（旁白）`);
      addPrefix(`${characterName}(旁白)`);
    }
  }

  if (dialogueType === 2) {
    addPrefix('内心OS');
    addPrefix('内心os');
    addPrefix('内心');
  }

  if (dialogueType === 3) {
    addPrefix('旁白');
    addPrefix('画外音');
  }

  return [...prefixes].sort((left, right) => right.length - left.length);
}

function stripKnownVoicePromptPrefix(
  text: string,
  options?: {
    characterName?: string | null;
    dialogueType?: 1 | 2 | 3;
  }
): string {
  for (const prefix of buildVoicePromptPrefixes(options)) {
    if (text.startsWith(prefix)) {
      return text.slice(prefix.length).trimStart();
    }
  }

  return text;
}

export function sanitizeVoicePromptText(
  text: string,
  options?: {
    characterName?: string | null;
    dialogueType?: 1 | 2 | 3;
  }
): string {
  let normalized = text.trim();
  if (!normalized) return normalized;

  normalized = normalized.replace(VOICE_PROMPT_TYPE_TAG_RE, '').trimStart();
  normalized = stripKnownVoicePromptPrefix(normalized, options);

  return normalized;
}

/** 对白角色音色（来自分镜关联的当前角色标签 meta_data.voice） */
export interface DialogueVoiceApiItem {
  voiceId?: string | null;
  providerVoiceId?: string | null;
  voiceName?: string | null;
}

export interface StoryboardDialogueApiItem {
  dialogueId: string;
  dialogue_id?: string;
  storyboardId: string;
  storyboard_id?: string;
  characterId?: string | null;
  character_id?: string | null;
  /** 角色音色来源标签ID（前端用于编辑音色时定位可写入的装扮记录） */
  characterTagId?: string | null;
  character_tag_id?: string | null;
  tagLabel?: string | null;
  tag_label?: string | null;
  characterName?: string | null;
  character_name?: string | null;
  characterImageUrl?: string | null;
  character_image_url?: string | null;
  content: string;
  dialogueType: number;
  dialogue_type?: number;
  sequenceOrder: number;
  sequence_order?: number;
  startTime?: string | null;
  start_time?: string | null;
  duration?: string | null;
  emotion?: string | null;
  notes?: string | null;
  /** 角色标签音色（来自分镜关联的当前角色标签，无则 null） */
  voice?: DialogueVoiceApiItem | null;
  createdAt: string;
  updatedAt: string;
}

export interface StoryboardDialogueViewItem {
  dialogueId: string;
  storyboardId: string;
  characterId?: string;
  /** 角色音色来源标签ID（前端用于编辑音色时定位可写入的装扮记录） */
  characterTagId?: string;
  tagLabel?: string;
  characterName?: string;
  characterImageUrl?: string;
  content: string;
  dialogueType: 1 | 2 | 3;
  sequenceOrder: number;
  startTime?: number;
  duration?: number;
  emotion?: string;
  notes?: string;
  /** 角色标签音色（来自分镜关联的当前角色标签，无则 null） */
  voice?: DialogueVoiceApiItem | null;
  createdAt: string;
  updatedAt: string;
}

const readNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

export function readDialogueCharacterImageUrl(
  rawItem: Record<string, unknown>
): string | undefined {
  return (
    readNonEmptyString(rawItem.characterImageUrl) ?? readNonEmptyString(rawItem.character_image_url)
  );
}

const toOptionalNumber = (value: string | null | undefined): number | undefined => {
  if (typeof value !== 'string') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeDialogueType = (value: number): 1 | 2 | 3 => {
  if (value === 2 || value === 3) return value;
  return 1;
};

export function mapStoryboardDialogueApiItem(
  item: StoryboardDialogueApiItem
): StoryboardDialogueViewItem {
  const rawItem = item as StoryboardDialogueApiItem & Record<string, unknown>;
  const dialogueType = item.dialogueType ?? item.dialogue_type ?? 1;
  const sequenceOrder = item.sequenceOrder ?? item.sequence_order ?? 0;
  return {
    dialogueId: item.dialogueId ?? item.dialogue_id,
    storyboardId: item.storyboardId ?? item.storyboard_id,
    characterId: item.characterId || item.character_id || undefined,
    characterTagId: item.characterTagId || item.character_tag_id || undefined,
    tagLabel: readNonEmptyString(item.tagLabel) ?? readNonEmptyString(item.tag_label),
    characterName: item.characterName || item.character_name || undefined,
    characterImageUrl: readDialogueCharacterImageUrl(rawItem),
    content: item.content,
    dialogueType: normalizeDialogueType(dialogueType),
    sequenceOrder,
    startTime: toOptionalNumber(item.startTime ?? item.start_time),
    duration: toOptionalNumber(item.duration),
    emotion: item.emotion || undefined,
    notes: item.notes || undefined,
    voice: item.voice ?? undefined,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

const DIALOGUE_TYPE_LABELS: Record<1 | 2 | 3, string> = {
  1: '普通对白',
  2: '内心OS',
  3: '旁白',
};

/** 将 dialogueType 数字映射为后端要求的字符串标签 */
export function mapDialogueTypeToLabel(type: 1 | 2 | 3): string {
  return DIALOGUE_TYPE_LABELS[type] ?? '普通对白';
}

/** 将 dialogueType 字符串标签反向映射为数字（mapDialogueTypeToLabel 的逆函数） */
export function mapDialogueTypeLabelToNumber(label: string): 1 | 2 | 3 {
  if (label === '内心OS') return 2;
  if (label === '旁白') return 3;
  return 1;
}

/** 后端 businessParams.dialogues 单条结构 */
export interface DialoguePolishBusinessItem {
  dialogue_id: string;
  content: string;
  dialogue_type: string;
  character_name: string;
  emotion?: string;
  sequence_order: number;
}

/** 从 StoryboardItem 构建 businessParams.dialogues 单条 */
export function toDialoguePolishBusinessItem(
  item: {
    id: string;
    name?: string | null;
    dialogue?: string | null;
    dialogueType?: 1 | 2 | 3;
    emotion?: string | null;
  },
  sequenceOrder: number
): DialoguePolishBusinessItem {
  const dialogueType = item.dialogueType ?? 1;
  return {
    dialogue_id: item.id,
    content: item.dialogue?.trim() || '（无对白文本）',
    dialogue_type: mapDialogueTypeToLabel(dialogueType),
    character_name: item.name?.trim() || '旁白',
    emotion: item.emotion?.trim() || undefined,
    sequence_order: sequenceOrder,
  };
}

/** 将 businessParams.dialogues 单条还原为 DialogueMentionItem，兼容 snake_case 和 camelCase */
export function fromDialoguePolishBusinessItemToMentionItem(item: Record<string, unknown>): {
  dialogueId?: string;
  characterName: string;
  content: string;
  dialogueType?: 1 | 2 | 3;
  emotion?: string;
  sequenceOrder?: number;
} {
  const dialogueId =
    typeof item.dialogue_id === 'string' && item.dialogue_id
      ? item.dialogue_id
      : typeof item.dialogueId === 'string' && item.dialogueId
        ? item.dialogueId
        : undefined;

  const characterName =
    typeof item.character_name === 'string' && item.character_name.trim()
      ? item.character_name.trim()
      : typeof item.characterName === 'string' && item.characterName.trim()
        ? item.characterName.trim()
        : '旁白';

  const content = typeof item.content === 'string' ? item.content : '（无对白文本）';

  const dialogueTypeLabel =
    typeof item.dialogue_type === 'string'
      ? item.dialogue_type
      : typeof item.dialogueType === 'string'
        ? item.dialogueType
        : '';

  const emotion =
    typeof item.emotion === 'string' && item.emotion.trim() ? item.emotion.trim() : undefined;

  const sequenceOrder =
    typeof item.sequence_order === 'number'
      ? item.sequence_order
      : typeof item.sequenceOrder === 'number'
        ? item.sequenceOrder
        : undefined;

  return {
    dialogueId,
    characterName,
    content,
    dialogueType: mapDialogueTypeLabelToNumber(dialogueTypeLabel),
    emotion,
    sequenceOrder,
  };
}

/** 从 DialogueMentionItem 构建 businessParams.dialogues 单条 */
export function fromDialogueMentionItemToBusiness(item: {
  characterName: string;
  content: string;
  dialogueId?: string;
  dialogueType?: 1 | 2 | 3;
  emotion?: string;
  sequenceOrder?: number;
}): DialoguePolishBusinessItem {
  const dialogueType = item.dialogueType ?? 1;
  return {
    dialogue_id: item.dialogueId ?? '',
    content: item.content?.trim() || '（无对白文本）',
    dialogue_type: mapDialogueTypeToLabel(dialogueType),
    character_name: item.characterName?.trim() || '旁白',
    emotion: item.emotion?.trim() || undefined,
    sequence_order: item.sequenceOrder ?? 0,
  };
}

export function buildSingleDialoguePolishPrompt(
  item: Omit<DialoguePolishPromptItem, 'order'>
): string {
  const originalDialogue = item.dialogue?.trim() || '（无对白文本）';
  const emotionInstruction = item.emotion?.trim()
    ? `重点强化“${item.emotion.trim()}”情绪表达。`
    : '';
  const notesInstruction = item.notes?.trim() ? `参考备注：${item.notes.trim()}` : '';

  return [
    '请在保留原意、角色身份和剧情推进的前提下，润色下面这句对白。',
    emotionInstruction,
    notesInstruction,
    `原对白：${originalDialogue}`,
  ]
    .filter(Boolean)
    .join('\n');
}

export function buildBatchDialoguePolishPrompt(items: DialoguePolishPromptItem[]): string {
  const normalizedItems = items.map((item) => ({
    order: item.order,
    name: item.name?.trim() || '旁白',
    dialogue: item.dialogue?.trim() || '（无对白文本）',
    emotion: item.emotion?.trim(),
    notes: item.notes?.trim(),
  }));

  const sections = normalizedItems.map((item) =>
    [
      `${item.order}. 角色：${item.name}`,
      `原对白：${item.dialogue}`,
      item.emotion ? `情绪：${item.emotion}` : '',
      item.notes ? `备注：${item.notes}` : '',
    ]
      .filter(Boolean)
      .join('\n')
  );

  return [
    `请按顺序逐条润色下面 ${normalizedItems.length} 条对白。`,
    '要求：保留原意、角色身份、剧情推进和人物关系，不要合并条目，不要改变编号。',
    '润色结果请严格保持与输入相同的编号顺序返回。',
    '',
    ...sections,
  ].join('\n');
}
